import PageHeader from "../components/PageHeader";
import { foodPrograms, stats } from "../lib/mockData";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Utensils, MapPin, HandHeart } from "lucide-react";
import { toast } from "sonner";

export default function Food() {
  return (
    <>
      <PageHeader
        eyebrow="Food Support"
        title="Nobody in our net goes hungry."
        description="Community kitchens, food banks and pantries you can support — or receive help from."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        {/* Hero card */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 rounded-3xl overflow-hidden border border-border/60 relative min-h-[280px]">
            <img
              src="https://images.pexels.com/photos/7156158/pexels-photo-7156158.jpeg"
              alt="Volunteer packing food"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/80 via-primary/50 to-transparent" />
            <div className="relative p-8 sm:p-10 text-primary-foreground">
              <Badge className="bg-white/20 text-white border-0 rounded-full mb-4">Live</Badge>
              <h2 className="font-display text-3xl sm:text-4xl font-bold max-w-md text-balance">
                {stats.mealsDelivered.toLocaleString()} meals shared this year.
              </h2>
              <p className="mt-3 text-primary-foreground/90 max-w-md">
                Every dollar and every hour turns into a warm meal for someone who needs it.
              </p>
              <Button
                onClick={() => toast.success("Redirecting to donation portal...")}
                data-testid="food-donate-btn"
                className="mt-6 rounded-full bg-white text-primary hover:bg-white/90"
              >
                Donate to feed 20 families
              </Button>
            </div>
          </div>
          <Card className="rounded-3xl p-6 border-border/60 shadow-none bg-secondary/10 flex flex-col justify-between">
            <div>
              <div className="h-11 w-11 rounded-2xl bg-secondary text-secondary-foreground flex items-center justify-center mb-4">
                <HandHeart size={20} />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">Need food support?</h3>
              <p className="text-sm text-muted-foreground">
                Request a meal delivery or find a nearby food pantry — completely anonymous and free.
              </p>
            </div>
            <Button
              onClick={() => toast.success("Match request submitted. Response within 4 hours.")}
              data-testid="food-request-btn"
              className="rounded-full mt-6 bg-secondary hover:bg-secondary/90 text-white"
            >
              Request support
            </Button>
          </Card>
        </div>

        {/* Programs */}
        <h3 className="font-display text-2xl font-semibold mb-6">Programs accepting help</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {foodPrograms.map((p, i) => (
            <Card
              key={p.id}
              data-testid={`food-program-${p.id}`}
              className={`fade-up stagger-${(i % 6) + 1} rounded-3xl border-border/60 shadow-none p-6 hover:shadow-md transition-shadow`}
            >
              <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Utensils size={18} />
              </div>
              <Badge
                className={`rounded-full text-[10px] border-0 mb-3 ${
                  p.status === "accepting-help" ? "bg-secondary/15 text-secondary" : "bg-primary/10 text-primary"
                }`}
              >
                {p.status === "accepting-help" ? "Needs volunteers" : "Accepting donations"}
              </Badge>
              <h4 className="font-display font-semibold">{p.name}</h4>
              <p className="text-sm text-muted-foreground mt-1">{p.type}</p>
              <div className="mt-4 pt-4 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><MapPin size={12} /> {p.location}</span>
                <span className="font-display font-semibold text-foreground">{p.served.toLocaleString()} served</span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </>
  );
}
